/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom, map} from 'rxjs';
import {DestroyRef, Component, DoCheck, Inject, inject, signal} from '@angular/core';
import {SignalPageState} from '../common/controller/signal-page-state';
import {resourceOptionParams} from '../common/controller/signal-admin-resource-api';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './earnings-account-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {createObjectTypeItemLink} from '../common/directives/adn-select/adn-select-item-link.util';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {attachEditPagePhase} from '../common/util/edit-page-phase';
import type {EditPagePhaseApi} from '../common/util/edit-page-phase';
import {compareById} from '../common/util/object-id-comparison';
import {
  applyFullPageSizesToModel,
  AX_PUBLISHER_CONTACT_USER_PERMISSIONS,
  expandCountries,
  FullPageDimensions,
  loadFullPageDimensionsFromModel,
  transparencyLanguageFromEarningsAccount
} from './earnings-account-commons';
import {
  buildApprovalNotificationTree,
  buildApprovalPreviewNotificationsFromHistoryItem,
  buildApprovalPreviewRowFromHistoryItem,
  canPreviewApprovalHistoryItem,
  getActionableApprovalCreatives,
  getApprovalHistoryApprovalClass,
  getApprovalHistoryApprovalLabel,
  getApprovalHistoryEntityType,
  getApprovalNotificationsForRow,
  getApprovalPayloadForHistoryItem,
  getApprovalPayloadForRow,
  getApprovalHistoryFilterRequest,
  getApprovalHistorySearchLabel,
  getApprovalPreviewHistoryQuery,
  getApprovalPreviewRow,
  getOppositeApprovalForHistoryItem,
  refreshApprovalNotificationRows,
  setApprovalNotificationRowAction,
  setApprovalNotificationRowsAction,
  APPROVAL_HISTORY_SEARCH_TYPES
} from './earnings-account-approval.util';
import {TeamApiService} from '../admin/team/team-api.service';
import {SearchApiService} from '../common/controller/search-api.service';
import {
  ApprovalPreviewCallbacks,
  SiteApprovalPreviewModalComponent
} from '../site/site-approval-preview-modal.component';
import {CreativeApiService} from '../creative/creative-api.service';
import {RoleApiService} from '../admin/role/role-api.service';
import {UserApiService} from '../admin/user/user-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {WINDOW} from '../angular-platform-providers';
import {EarningsAccountApiService, EarningsAccountModel} from './earnings-account-api.service';
import {BlocklistApiService} from '../blocklist/blocklist-api.service';
import {RulesetApiService} from '../ruleset/ruleset-api.service';
import {FormsModule} from '@angular/forms';
import {UIRouterModule} from '@uirouter/angular';
import {APP_SHARED} from '../common/app-shared';
import {ADMIN_LIST} from '../common/directives/admin-list';
import {AdnSelectComponent} from '../common/directives/adn-select/adn-select.component';
import {AdnCountrySelectComponent} from '../common/directives/country-select/adn-country-select.component';
import {FORM_CONTROLS} from '../common/directives/form-controls/form-controls';
import {SearchSelectComponent} from '../common/controller/search-select.component';
import {ButtonsModule} from 'ngx-bootstrap/buttons';

@Component({
  standalone: true,
  imports: [
    ...APP_SHARED,
    FormsModule,
    UIRouterModule,
    ...ADMIN_LIST,
    AdnSelectComponent,
    AdnCountrySelectComponent,
    ...FORM_CONTROLS,
    SearchSelectComponent,
    ButtonsModule
  ],
  selector: 'adn-earnings-account-edit-page',
  template
})
export class EarningsAccountEditPageComponent extends SignalAdminResourceEditPageBase<EarningsAccountModel> implements DoCheck {
  readonly rulesetItemLink = createObjectTypeItemLink('Ruleset');
  readonly blocklistItemLink = createObjectTypeItemLink('Blocklist');

  isAxPublisher = false;
  canEditAdvertisingAccess = false;
  pureMarketplacePlatform = false;
  isPoliticalAdsPlatform = false;
  showFullPageOptions = false;
  showCountries = false;
  networkCurrencies: string[] = [];

  private readonly teamsState = signal<any[]>([]);
  private readonly axPublisherContactUsersState = signal<any[] | undefined>(undefined);
  private readonly blocklistsState = signal<any[] | false>(false);
  private readonly rulesetsState = signal<any[] | false>(false);
  private readonly approvalNotificationsState = signal<any[]>([]);
  private readonly approvalNotificationTreeState = signal<any[]>([]);
  private readonly approvalNotificationRowsState = signal<any[]>([]);
  private readonly acknowledgingApprovalNotificationsState = signal(false);
  private readonly approvingApprovalNotificationsState = signal(false);
  private readonly acknowledgingAllApprovalNotificationsState = signal(false);
  private readonly approvalNotificationsLoadingState = signal(false);
  private readonly approvalNotificationsErrorState = signal(false);
  private readonly approvalHistoryUpdatingState = signal(false);
  private readonly approvalHistorySearchResultsState = signal<any[]>([]);
  private readonly transparencyPreviewUrlState = signal('');

  get teams(): any[] { return this.teamsState(); }
  set teams(value: any[]) { this.teamsState.set(value); }
  get axPublisherContactUsers(): any[] | undefined { return this.axPublisherContactUsersState(); }
  set axPublisherContactUsers(value: any[] | undefined) { this.axPublisherContactUsersState.set(value); }
  get blocklists(): any[] | false { return this.blocklistsState(); }
  set blocklists(value: any[] | false) { this.blocklistsState.set(value); }
  get rulesets(): any[] | false { return this.rulesetsState(); }
  set rulesets(value: any[] | false) { this.rulesetsState.set(value); }
  get approvalNotifications(): any[] { return this.approvalNotificationsState(); }
  set approvalNotifications(value: any[]) { this.approvalNotificationsState.set(value); }
  get approvalNotificationTree(): any[] { return this.approvalNotificationTreeState(); }
  set approvalNotificationTree(value: any[]) { this.approvalNotificationTreeState.set(value); }
  get approvalNotificationRows(): any[] { return this.approvalNotificationRowsState(); }
  set approvalNotificationRows(value: any[]) { this.approvalNotificationRowsState.set(value); }
  get acknowledgingApprovalNotifications(): boolean { return this.acknowledgingApprovalNotificationsState(); }
  set acknowledgingApprovalNotifications(value: boolean) { this.acknowledgingApprovalNotificationsState.set(value); }
  get approvingApprovalNotifications(): boolean { return this.approvingApprovalNotificationsState(); }
  set approvingApprovalNotifications(value: boolean) { this.approvingApprovalNotificationsState.set(value); }
  get acknowledgingAllApprovalNotifications(): boolean { return this.acknowledgingAllApprovalNotificationsState(); }
  set acknowledgingAllApprovalNotifications(value: boolean) { this.acknowledgingAllApprovalNotificationsState.set(value); }
  get approvalNotificationsLoading(): boolean { return this.approvalNotificationsLoadingState(); }
  set approvalNotificationsLoading(value: boolean) { this.approvalNotificationsLoadingState.set(value); }
  get approvalNotificationsError(): boolean { return this.approvalNotificationsErrorState(); }
  set approvalNotificationsError(value: boolean) { this.approvalNotificationsErrorState.set(value); }
  get approvalHistoryUpdating(): boolean { return this.approvalHistoryUpdatingState(); }
  set approvalHistoryUpdating(value: boolean) { this.approvalHistoryUpdatingState.set(value); }
  get approvalHistorySearchResults(): any[] { return this.approvalHistorySearchResultsState(); }
  set approvalHistorySearchResults(value: any[]) { this.approvalHistorySearchResultsState.set(value); }
  siteRulesets: any[] = [];
  targetingRulesets: any[] = [];
  sizeRulesets: any[] = [];
  layoutRulesets: any[] = [];

  canManageApprovalNotifications = false;
  showApprovals = false;
  readonly approvalHistory = new SignalPageState<any, {id: string; approvalLevel: string; filter: any}>(
    (query, page, pageSize) => this.earningsAccountApi.getApprovalHistory(query.id,
      {page, pageSize, ...(query.approvalLevel ? {approvalLevel: query.approvalLevel} : {})}, query.filter
    ).pipe(map((response) => ({...response, results: (response.results || []).map((item) => ({
      ...item, entityType: getApprovalHistoryEntityType(item)
    }))}))), 20, inject(DestroyRef), true
  );
  get approvalHistoryLoading(): boolean { return this.approvalHistory.loading(); }
  get approvalHistoryLoaded(): boolean { return this.approvalHistory.loaded(); }
  get approvalHistoryError(): boolean { return this.approvalHistory.loadFailed(); }
  set approvalHistoryError(value: boolean) { this.approvalHistory.loadFailed.set(value); }
  approvalHistoryApprovalLevel: string = '';
  approvalHistoryFilter: any = null;
  readonly approvalLevelOptions = [
    {value: '', label: 'All object types'},
    {value: 'ADVERTISER', label: 'Advertisers'},
    {value: 'TEAM', label: 'Teams'},
    {value: 'ORDER', label: 'Orders'},
    {value: 'LINE_ITEM', label: 'Line Items'},
    {value: 'CREATIVE', label: 'Creatives'}
  ];

  fullPageDims: FullPageDimensions = {};

  /** Attached in constructor — template gates the form with [query]="editPhaseQuery". */
  editPhaseQuery: {$resolved: boolean} = {$resolved: false};
  private editPhaseApi: EditPagePhaseApi;

  private axPublisherRoleIdsPromise: Promise<string[]> | undefined;
  private usersPromise: Promise<any[]> | undefined;
  private contactUsersTeamId: string | undefined;
  private pendingAsyncLoads = 0;
  /** Sync re-entry guard; template uses the deferred public busy flags. */
  private approvalActionInFlight = false;
  /** Sync guards while loading flags are deferred for the template. */
  private approvalNotificationsLoadInFlight = false;
  private approvalHistoryAccountId: string;

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly translate: TranslateService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly blocklistApi: BlocklistApiService,
    private readonly rulesetApi: RulesetApiService,
    private readonly teamApi: TeamApiService,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    private readonly userApi: UserApiService,
    private readonly roleApi: RoleApiService,
    private readonly earningsAccountApi: EarningsAccountApiService,
    private readonly searchApi: SearchApiService,
    @Inject(WINDOW) private readonly window: any,
    private readonly angularModalService: AngularModalService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(inject(EarningsAccountApiService), 'earningsAccount');

    this.isAxPublisher = LocalUserPermissions.isAnyAxPublisher();
    this.canEditAdvertisingAccess = LocalUserPermissions.hasNetwork('MANAGE_BUSINESS')
      || LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');
    this.pureMarketplacePlatform = LocalNetworkProfile.isPureMarketplacePlatform();
    this.isPoliticalAdsPlatform = LocalNetworkProfile.isPoliticalAdsPlatform();

    const isSpecialUser = localUserProfile.get('username') === 'adnuntius@adnuntius.com'
      || localUserProfile.get('username') === 'broker1@adnuntius.com';
    const isLocalhost = this.window.location.hostname === 'localhost';
    this.showFullPageOptions = isLocalhost || (this.pureMarketplacePlatform && isSpecialUser);
    this.showCountries = this.localNetworkInfo.getSiteCountries().length > 1;

    const defaultCurrency = LocalNetworkProfile.get('defaultCurrency');
    this.networkCurrencies = _.orderBy(LocalNetworkProfile.get('availableCurrencies'), (curr: string) => {
      return curr === defaultCurrency ? 0 : 1;
    });

    this.editPhaseApi = attachEditPagePhase(this);
    this.editPhaseApi.wait('async');
    this.pendingAsyncLoads = this.pureMarketplacePlatform ? 3 : 2;
    this.loadBlocklists();
    if (this.pureMarketplacePlatform) {
      this.loadRulesets();
    }
    this.loadTeams();
  }

  get showPublisherTransparencyFields(): boolean {
    return (this.LocalNetworkProfile.hasPoliticalCapability() || this.pureMarketplacePlatform)
      && this.editingModel?.politicalAdvertising !== 'BLOCKED';
  }

  get transparencyPreviewUrl(): string {
    return this.transparencyPreviewUrlState();
  }

  ngDoCheck(): void {
    const url = this.computeTransparencyPreviewUrl();
    if (url !== this.transparencyPreviewUrlState()) {
      this.transparencyPreviewUrlState.set(url);
    }
  }

  private computeTransparencyPreviewUrl(): string {
    const publisherId = _.get(this.editingModel, 'id');
    const networkTag = this.LocalNetworkProfile.getNetworkTag();
    let url = '';
    if (!publisherId || !networkTag) {
      return url;
    }
    url = 'https://adtransparency.eu/transparency-preview?n='
      + encodeURIComponent(networkTag)
      + '&pe=' + encodeURIComponent(publisherId)
      + '&preview=true&political=true';
    const language = transparencyLanguageFromEarningsAccount(this.editingModel);
    if (language) {
      url += '&lang=' + encodeURIComponent(language);
    }
    return url;
  }

  getApprovalObjectTypePrefix = (type: string): string => {
    return this.getApprovalTypeLabel({type}) + ': ';
  };

  getApprovalTypeLabel(row: any): string {
    if (!row || !row.type) {
      return 'Object';
    }
    return this.translate.instant(row.type + '.' + row.type);
  }

  getApprovalHistoryApprovalLabel = getApprovalHistoryApprovalLabel;
  getApprovalHistoryApprovalClass = getApprovalHistoryApprovalClass;
  canPreviewApprovalHistoryItem = canPreviewApprovalHistoryItem;

  get showApprovalHistoryFilters(): boolean {
    if (this.approvalHistoryFilter || this.approvalHistoryApprovalLevel) {
      return true;
    }
    return (this.approvalHistory.totalCount() || 0) > (this.approvalHistory.pageSize || 20);
  }

  getApprovalHistoryPendingMessage(historyItem: any): string {
    const typeLabel = this.getApprovalTypeLabel({type: _.get(historyItem, 'entityType')});
    return 'The ' + typeLabel + ' will be available for review again once it becomes a candidate for delivery';
  }

  shouldShowApprovalHistoryToggle(): boolean {
    if (!this.canManageApprovalNotifications) {
      return false;
    }
    if (this.editingModel.politicalAdvertising === 'REQUIRES_APPROVAL') {
      return true;
    }
    if (this.approvalHistoryError) {
      return true;
    }
    if (!this.approvalHistoryLoaded) {
      return false;
    }
    return (this.approvalHistory.totalCount() || 0) > 0;
  }

  teamChange(): void {
    this.initialiseAxPublisherContactUsers(false);
    this.markExternalFormDirty();
  }

  toggleApprovalNotificationGroup(group: any): void {
    if (!group || _.isEmpty(group.children)) {
      return;
    }
    group.expanded = !group.expanded;
    this.approvalNotificationRows = refreshApprovalNotificationRows(this.approvalNotificationTree);
  }

  rebuildApprovalNotificationTree(): void {
    this.approvalNotificationTree = buildApprovalNotificationTree(this.approvalNotifications, this.approvalNotificationTree);
    this.approvalNotificationRows = refreshApprovalNotificationRows(this.approvalNotificationTree);
  }

  loadApprovalNotifications(): void {
    if (!this.canManageApprovalNotifications || !this.editingModel?.id || this.approvalNotificationsLoadInFlight) {
      return;
    }
    this.approvalNotificationsLoadInFlight = true;
    this.approvalNotificationsLoading = true;
    this.approvalNotificationsError = false;
    firstValueFrom(this.earningsAccountApi.getApprovalNotifications(this.editingModel.id)).then((response) => {
      this.approvalNotifications = response.needsApproval || response.creatives || [];
      this.rebuildApprovalNotificationTree();
    }, () => {
      this.approvalNotificationsError = true;
    }).finally(() => {
      this.approvalNotificationsLoadInFlight = false;
      this.approvalNotificationsLoading = false;
    });
  }

  onApprovalLevelChange(level: string): void {
    this.approvalHistoryApprovalLevel = level;
    this.reloadApprovalHistory();
  }

  onApprovalHistoryFilterChange(): void {
    this.reloadApprovalHistory();
  }

  clearApprovalHistoryFilters(): void {
    if (!this.approvalHistoryFilter && !this.approvalHistoryApprovalLevel) {
      return;
    }
    this.approvalHistoryFilter = null;
    this.approvalHistoryApprovalLevel = '';
    this.reloadApprovalHistory();
  }

  searchApprovalHistoryObjects = (searchElement: AdnSelectSearchElement): void => {
    this.searchApi.search({
      q: searchElement.search,
      types: APPROVAL_HISTORY_SEARCH_TYPES,
      pageSize: 50
    }).subscribe({next: (data) => {
      const results = _.map(data.searchResults || [], (result: any) => {
        result.displayName = getApprovalHistorySearchLabel(result);
        return result;
      });
      const selected = this.approvalHistoryFilter;
      if (selected?.id && !_.some(results, ['id', selected.id])) {
        if (!selected.displayName) {
          selected.displayName = getApprovalHistorySearchLabel(selected);
        }
        results.unshift(selected);
      }
      this.approvalHistorySearchResults = results;
    }, error: () => {
      this.approvalHistorySearchResults = [];
    }});
  };

  loadApprovalHistory(): void {
    if (!this.canManageApprovalNotifications || this.isNew || !this.editingModel?.id) {
      this.approvalHistory.clear();
      this.approvalHistoryAccountId = undefined;
      return;
    }
    if (this.approvalHistoryAccountId !== this.editingModel.id) {
      this.approvalHistory.clear();
      this.approvalHistoryAccountId = this.editingModel.id;
    }
    this.approvalHistory.load({id: this.editingModel.id,
      approvalLevel: this.approvalHistoryApprovalLevel,
      filter: getApprovalHistoryFilterRequest(this.approvalHistoryFilter)});
  }

  openApprovalPreview(row: any): void | Promise<void> {
    if (row && row.entityType && !row.type) {
      const historyPreviewQuery = getApprovalPreviewHistoryQuery(row);
      if (!historyPreviewQuery) {
        return this.openApprovalPreviewModal(
          getApprovalPreviewRow(row),
          buildApprovalPreviewNotificationsFromHistoryItem(row)
        );
      }
      return firstValueFrom(this.Creative.listOptions(resourceOptionParams(historyPreviewQuery))).then((page: any) => {
        const creatives = _.get(page, 'results') || [];
        return this.openApprovalPreviewModal(
          buildApprovalPreviewRowFromHistoryItem(row, creatives),
          buildApprovalPreviewNotificationsFromHistoryItem(row, creatives)
        );
      });
    }
    const previewRow = getApprovalPreviewRow(row);
    const previewNotifications = getApprovalNotificationsForRow(previewRow);
    return this.openApprovalPreviewModal(previewRow, previewNotifications);
  }

  updateApprovalHistoryItem(historyItem: any): void {
    const approval = getOppositeApprovalForHistoryItem(historyItem);
    const approvalPayload = getApprovalPayloadForHistoryItem(historyItem, approval as string);
    if (!approvalPayload || this.approvalHistoryUpdating || this.isApprovalActionBusy()) {
      return;
    }
    this.approvalHistoryUpdating = true;
    this.approvalHistoryError = false;
    historyItem.updating = true;
    firstValueFrom(this.earningsAccountApi.recordApproval(this.editingModel.id, approvalPayload)).then(() => {
      this.loadApprovalNotifications();
      this.loadApprovalHistory();
    }, () => {
      this.approvalHistoryError = true;
    }).finally(() => {
      this.approvalHistoryUpdating = false;
      historyItem.updating = false;
    });
  }

  acknowledgeApprovalNotifications(row: any): void {
    const creatives = getActionableApprovalCreatives(getApprovalNotificationsForRow(row));
    if (_.isEmpty(creatives) || this.isApprovalActionBusy()) {
      return;
    }
    this.approvalActionInFlight = true;
    this.setApprovalBusyFlag('acknowledgingApprovalNotifications', true, () => {
      setApprovalNotificationRowAction(row, creatives, 'acknowledging', true);
    });
    this.approvalNotificationsError = false;
    firstValueFrom(this.earningsAccountApi.acknowledgeApprovalNotifications(this.editingModel.id, {
      creatives: _.map(creatives, (creative) => ({id: creative.id}))
    })).then(() => {
      this.removeApprovalNotificationsForCreatives(creatives);
      this.rebuildApprovalNotificationTree();
    }, () => {
      this.approvalNotificationsError = true;
    }).finally(() => {
      this.approvalActionInFlight = false;
      this.setApprovalBusyFlag('acknowledgingApprovalNotifications', false, () => {
        setApprovalNotificationRowAction(row, creatives, 'acknowledging', false);
      });
    });
  }

  approveApprovalNotifications(row: any, approval: string, creativesOverride?: any[]): any {
    const creatives = getActionableApprovalCreatives(
      _.isArray(creativesOverride) ? creativesOverride : getApprovalNotificationsForRow(row)
    );
    const approvalPayload = getApprovalPayloadForRow(row, approval);
    if (!approvalPayload || _.isEmpty(creatives) || this.isApprovalActionBusy()) {
      return null;
    }
    this.approvalActionInFlight = true;
    this.setApprovalBusyFlag('approvingApprovalNotifications', true, () => {
      setApprovalNotificationRowAction(row, creatives, 'approving', true);
    });
    this.approvalNotificationsError = false;
    return firstValueFrom(this.earningsAccountApi.recordApproval(this.editingModel.id, approvalPayload)).then(() => {
      if (approval === 'PENDING') {
        this.loadApprovalNotifications();
      } else {
        this.removeApprovalNotificationsForCreatives(creatives);
        this.rebuildApprovalNotificationTree();
      }
      return {approvedCreativeIds: _.map(creatives, 'id')};
    }, () => {
      this.approvalNotificationsError = true;
      return Promise.reject();
    }).finally(() => {
      this.approvalActionInFlight = false;
      this.setApprovalBusyFlag('approvingApprovalNotifications', false, () => {
        setApprovalNotificationRowAction(row, creatives, 'approving', false);
      });
    });
  }

  approveCreativeApprovalNotifications(rows: any[], approval: string): any {
    const creativeRows = _.filter(rows || [], (row) => {
      return !!_.get(row, 'creative.id') && !!getApprovalPayloadForRow(row, approval);
    });
    if (_.isEmpty(creativeRows) || this.isApprovalActionBusy()) {
      return null;
    }
    this.approvalActionInFlight = true;
    this.approvalNotificationsError = false;
    this.setApprovalBusyFlag('approvingApprovalNotifications', true, () => {
      setApprovalNotificationRowsAction(creativeRows, 'approving', true);
    });

    const approvedCreatives: any[] = [];
    const failures: any[] = [];

    const processNext = (index: number): any => {
      if (index >= creativeRows.length) {
        return failures.length ? Promise.reject({
          approvedCreativeIds: _.map(approvedCreatives, 'id'),
          message: 'Updated ' + approvedCreatives.length + ' of ' + creativeRows.length + ' individual creative' + (creativeRows.length === 1 ? '' : 's') + '.'
        }) : Promise.resolve({
          approvedCreativeIds: _.map(approvedCreatives, 'id')
        });
      }
      const creativeRow = creativeRows[index];
      const payload = getApprovalPayloadForRow(creativeRow, approval);
      return firstValueFrom(this.earningsAccountApi.recordApproval(this.editingModel.id, payload)).then(() => {
        approvedCreatives.push(creativeRow.creative);
      }, () => {
        failures.push(creativeRow);
      }).then(() => processNext(index + 1));
    };

    return processNext(0).then((result: any) => {
      if (approval === 'PENDING') {
        this.loadApprovalNotifications();
      } else {
        this.removeApprovalNotificationsForCreatives(approvedCreatives);
        this.rebuildApprovalNotificationTree();
      }
      return result;
    }, (result: any) => {
      if (approval === 'PENDING') {
        this.loadApprovalNotifications();
      } else {
        this.removeApprovalNotificationsForCreatives(approvedCreatives);
        this.rebuildApprovalNotificationTree();
      }
      this.approvalNotificationsError = failures.length > 0;
      return Promise.reject(result);
    }).finally(() => {
      this.approvalActionInFlight = false;
      this.setApprovalBusyFlag('approvingApprovalNotifications', false, () => {
        setApprovalNotificationRowsAction(creativeRows, 'approving', false);
      });
    });
  }

  acknowledgeAllApprovalNotifications(): void {
    const creatives = getActionableApprovalCreatives(this.approvalNotifications);
    if (_.isEmpty(creatives) || this.isApprovalActionBusy()) {
      return;
    }
    this.approvalActionInFlight = true;
    this.setApprovalBusyFlag('acknowledgingAllApprovalNotifications', true);
    this.approvalNotificationsError = false;
    firstValueFrom(this.earningsAccountApi.acknowledgeApprovalNotifications(this.editingModel.id, {
      creatives: _.map(creatives, (creative) => ({id: creative.id}))
    })).then(() => {
      this.approvalNotifications = [];
      this.rebuildApprovalNotificationTree();
    }, () => {
      this.approvalNotificationsError = true;
    }).finally(() => {
      this.approvalActionInFlight = false;
      this.setApprovalBusyFlag('acknowledgingAllApprovalNotifications', false);
    });
  }

  protected override afterEditingModelReset(): void {
    this.editingModel.address = this.editingModel.address || {country: ''};
    this.editingModel.contactUsers = this.editingModel.contactUsers || [];
    this.editingModel.filterTeamsType = this.editingModel.filterTeamsType || 'NONE';
    if (!this.editingModel.politicalAdvertising) {
      this.editingModel.politicalAdvertising = 'ALLOWED';
    }
    this.editingModel.countriesExpanded = expandCountries(this.editingModel);
    this.initApprovalSettings();
    this.applyFullPageDimsFromModel();
    this.initialiseAxPublisherContactUsers(true);
    this.resolveTeamReference();
    this.resolveReferences();
  }

  protected override prepareForSubmit(model: EarningsAccountModel): void {
    if (!_.get(model, ['team', 'id'])) {
      model.contactUsers = [];
    } else if (this.axPublisherContactUsers) {
      this.sanitiseContactUsers(model);
    }
    if (model.politicalAdvertising === 'ALLOWED') {
      model.politicalAdvertising = null;
    }
    applyFullPageSizesToModel(model, this.fullPageDims);
  }

  onTeamModelChange(): void {
    this.teamChange();
  }

  readonly compareById = compareById;

  onPoliticalAdvertisingChange(): void {
    this.markExternalFormDirty();
  }

  onFullPageDimChange(): void {
    this.markExternalFormDirty();
  }

  countryChangeHook = (): void => {
    this.markExternalFormDirty();
  };

  trackByApprovalKey(_index: number, item: any): string {
    return item.key;
  }

  private reloadApprovalHistory(): void {
    this.approvalHistory.currentPage.set(1);
    this.loadApprovalHistory();
  }

  private initApprovalSettings(): void {
    const isLocalhost = this.window.location.hostname === 'localhost';
    const politicalBlockingEnabled = this.editingModel.politicalAdvertising !== 'ALLOWED';
    this.canManageApprovalNotifications = (politicalBlockingEnabled || this.pureMarketplacePlatform
      || this.LocalNetworkProfile.isExperimental() || isLocalhost)
      && !this.isNew
      && (this.LocalUserPermissions.hasAny('MANAGE_PUBLISHING') || this.LocalUserPermissions.hasAny('AX_PUBLISHER'));

    if (this.canManageApprovalNotifications && this.editingModel?.id) {
      this.loadApprovalNotifications();
      this.loadApprovalHistory();
    }
  }

  private openApprovalPreviewModal(previewRow: any, previewNotifications: any[]): void {
    const creatives = getActionableApprovalCreatives(previewNotifications);
    if (_.isEmpty(creatives)) {
      return;
    }
    const callbacks: ApprovalPreviewCallbacks = {
      approveApprovalNotifications: (row, approval, override) => this.approveApprovalNotifications(row, approval, override),
      approveCreativeApprovalNotifications: (rows, approval) => this.approveCreativeApprovalNotifications(rows, approval),
      getApprovalObjectTypePrefix: (type) => this.getApprovalObjectTypePrefix(type),
      getApprovalTypeLabel: (row) => this.getApprovalTypeLabel(row)
    };
    const modalRef = this.angularModalService.show(SiteApprovalPreviewModalComponent, {
      class: 'modal-lg',
      initialState: {
        row: previewRow,
        previewNotifications,
        callbacks,
        onClose: () => {
          this.loadApprovalNotifications();
          this.loadApprovalHistory();
        }
      }
    });
    void modalRef;
  }

  private removeApprovalNotificationsForCreatives(creatives: any[]): void {
    const creativeIds = _.keyBy(creatives, 'id');
    _.remove(this.approvalNotifications, (existingNotification) => {
      const creative = _.has(existingNotification, 'creative')
        ? _.get(existingNotification, 'creative')
        : existingNotification;
      return !!creativeIds[_.get(creative, 'id')];
    });
  }

  private getCurrentTeamId(): string | undefined {
    return _.get(this.editingModel, ['team', 'id']);
  }

  private isApprovalActionBusy(): boolean {
    return this.approvalActionInFlight
      || this.approvingApprovalNotifications
      || this.acknowledgingApprovalNotifications
      || this.acknowledgingAllApprovalNotifications;
  }

  private setApprovalBusyFlag(
    flag: 'approvingApprovalNotifications' | 'acknowledgingApprovalNotifications' | 'acknowledgingAllApprovalNotifications',
    value: boolean,
    beside?: () => void
  ): void {
    this[flag] = value;
    if (beside) {
      beside();
    }
  }

  private getAxPublisherRoleIds(): Promise<string[]> {
    if (!this.axPublisherRoleIdsPromise) {
      this.axPublisherRoleIdsPromise = firstValueFrom(this.roleApi.listOptions()).then((rolePage) => {
        return _.map(_.filter(rolePage.results, (role: any) => {
          return _.some(_.get(role, 'permissions', []), (permission: string) => {
            return AX_PUBLISHER_CONTACT_USER_PERMISSIONS.indexOf(permission) > -1;
          });
        }), 'id');
      }, () => []);
    }
    return this.axPublisherRoleIdsPromise;
  }

  private getUsers(): Promise<any[]> {
    if (!this.usersPromise) {
      this.usersPromise = firstValueFrom(this.userApi.listOptions({
        team: this.getCurrentTeamId(),
        permissions: AX_PUBLISHER_CONTACT_USER_PERMISSIONS,
        concise: true,
        pageSize: this.localNetworkInfo.getInfo('userCount') + 50
      })).then((userPage) => userPage.results || [], () => []);
    }
    return this.usersPromise;
  }

  private hasAxPublisherTeamRole(user: any, publisherRoleIds: string[], teamId: string): boolean {
    const networkId = this.LocalNetworkProfile.getNetworkId();
    const rolesByNetwork = _.find(_.get(user, ['userRoles', 'rolesByNetwork'], []), (rolesForNetwork: any) => {
      return _.get(rolesForNetwork, ['network', 'id']) === networkId
        || _.get(rolesForNetwork, ['network', 'tagId']) === networkId;
    });
    return !!_.find(_.get(rolesByNetwork, 'roles', []), (roleEntry: any) => {
      return _.get(roleEntry, ['team', 'id']) === teamId
        && publisherRoleIds.indexOf(_.get(roleEntry, ['role', 'id'])) > -1;
    });
  }

  private sanitiseContactUsers(model: any = this.editingModel): void {
    // Keep selection as the same object instances from [items] so adn-select can
    // show chips/labels after the async user list loads.
    const selectedIds = _.keyBy(model.contactUsers || [], 'id');
    model.contactUsers = _.filter(this.axPublisherContactUsers || [], (user: any) => {
      return !!selectedIds[user.id];
    });
  }

  private initialiseAxPublisherContactUsers(preserveSelection: boolean): void {
    const teamId = this.getCurrentTeamId();
    if (this.contactUsersTeamId !== teamId) {
      this.usersPromise = undefined;
      this.contactUsersTeamId = teamId;
    }
    this.axPublisherContactUsers = undefined;
    if (!teamId) {
      this.axPublisherContactUsers = [];
      this.editingModel.contactUsers = [];
      return;
    }
    if (!preserveSelection) {
      this.editingModel.contactUsers = [];
    }
    this.getAxPublisherRoleIds().then((publisherRoleIds) => {
      return this.getUsers().then((users) => {
        return _.orderBy(_.filter(users, (user) => {
          return this.hasAxPublisherTeamRole(user, publisherRoleIds, teamId as string);
        }), [(user) => user.displayName || user.name || user.username]);
      });
    }).then((users) => {
      if (this.getCurrentTeamId() !== teamId) {
        return;
      }
      this.axPublisherContactUsers = users;
      this.sanitiseContactUsers();
    }, () => {
      if (this.getCurrentTeamId() !== teamId) {
        return;
      }
      this.axPublisherContactUsers = [];
      this.editingModel.contactUsers = [];
    });
  }

  private applyFullPageDimsFromModel(): void {
    this.fullPageDims = loadFullPageDimensionsFromModel(this.editingModel);
  }

  private loadBlocklists(): void {
    this.blocklistApi.list({
      includeHidden: false,
      includeActive: true,
      includeInactive: false,
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: 1,
      pageSize: 1001,
      concise: false,
      minimal: true
    }).subscribe({
      next: (page) => {
        this.blocklists = page.results || [];
        this.resolveBlocklistReference();
        this.onAsyncLoadComplete();
      },
      error: () => {
        this.blocklists = [];
        this.onAsyncLoadComplete();
      }
    });
  }

  private loadRulesets(): void {
    this.rulesetApi.list({
      includeHidden: false,
      includeActive: true,
      includeInactive: false,
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: 1,
      pageSize: 1001,
      concise: false,
      minimal: false
    }).subscribe({
      next: (page) => {
        const results = page.results || [];
        this.rulesets = results;
        this.siteRulesets = _.filter(results, (rs: any) => rs.type === 'INVENTORY');
        this.targetingRulesets = _.filter(results, (rs: any) => rs.type === 'TARGETING');
        this.sizeRulesets = _.filter(results, (rs: any) => rs.type === 'SIZE');
        this.layoutRulesets = _.filter(results, (rs: any) => rs.type === 'LAYOUT');
        this.resolveRulesetReferences();
        this.onAsyncLoadComplete();
      },
      error: () => {
        this.rulesets = [];
        this.siteRulesets = [];
        this.targetingRulesets = [];
        this.sizeRulesets = [];
        this.layoutRulesets = [];
        this.onAsyncLoadComplete();
      }
    });
  }

  private loadTeams(): void {
    this.teamApi.listOptions({where: 'type in STANDARD,AX_PUBLISHER'}).subscribe((page) => {
      const allTeams = page.results || [];
      const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
      this.teams = isViewAllObjects ? allTeams : _.filter(allTeams, (g: any) => {
        return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
      });
      if (this.isAxPublisher && allTeams.length > 0 && this.teams.length > 0 && !this.editingModel.team) {
        this.editingModel.team = this.teams[0];
        this.initialiseAxPublisherContactUsers(true);
      }
      this.resolveTeamReference();
      this.initialiseAxPublisherContactUsers(true);
      this.onAsyncLoadComplete();
    }, () => {
      this.teams = [];
      this.onAsyncLoadComplete();
    });
  }

  private onAsyncLoadComplete(): void {
    this.pendingAsyncLoads--;
    if (this.pendingAsyncLoads <= 0) {
      this.editPhaseApi.done('async');
    }
  }

  private resolveReferences(): void {
    this.resolveBlocklistReference();
    this.resolveRulesetReferences();
  }

  private resolveTeamReference(): void {
    if (!this.editingModel?.team || !this.teams.length) {
      return;
    }
    const teamId = _.isString(this.editingModel.team)
      ? this.editingModel.team
      : this.editingModel.team.id;
    const match = _.find(this.teams, ['id', teamId]);
    if (match) {
      this.editingModel.team = match;
    } else if (!_.isString(this.editingModel.team)) {
      this.teams.push(this.editingModel.team);
    }
  }

  private resolveBlocklistReference(): void {
    if (!_.isArray(this.blocklists) || !this.editingModel?.blocklist?.id) {
      return;
    }
    const match = _.find(this.blocklists as any[], ['id', this.editingModel.blocklist.id]);
    if (match) {
      this.editingModel.blocklist = match;
    }
  }

  private resolveRulesetReferences(): void {
    if (!_.isArray(this.rulesets) || !this.editingModel?.siteRuleset?.id) {
      return;
    }
    const siteRuleset = _.find(this.rulesets as any[], ['id', this.editingModel.siteRuleset.id]);
    if (siteRuleset) {
      this.editingModel.siteRuleset = siteRuleset;
    }
  }
}
